import { encodeFlowCode } from './flowLink';

/**
 * Rewrite a backend handoff URL so the QR always points at the hostname the
 * applicant is currently on. Backend may return a relative path (`/resume/…`)
 * or an absolute URL built from `verify_base_url` (often wrong in local/tunnel
 * dev). Phone scanning must hit the same origin as this SPA.
 *
 * Keeps pathname + search + hash; only the origin is replaced (or filled in).
 */
export function resolveHandoffUrl(
  handoffUrl: string,
  origin: string = typeof window !== 'undefined' ? window.location.origin : '',
): string {
  const raw = handoffUrl.trim();
  if (!raw) return raw;

  try {
    if (/^https?:\/\//i.test(raw)) {
      const remote = new URL(raw);
      return `${stripTrailingSlash(origin)}${remote.pathname}${remote.search}${remote.hash}`;
    }
    // Protocol-relative //host/path — rare, still rewrite to current origin path.
    if (raw.startsWith('//')) {
      const remote = new URL(`https:${raw}`);
      return `${stripTrailingSlash(origin)}${remote.pathname}${remote.search}${remote.hash}`;
    }
    const path = raw.startsWith('/') ? raw : `/${raw}`;
    return `${stripTrailingSlash(origin)}${path}`;
  } catch {
    // Malformed input — leave as-is rather than invent a URL.
    return raw;
  }
}

function stripTrailingSlash(origin: string): string {
  return origin.replace(/\/$/, '');
}

/**
 * The long-lived collection-session entry URL for THIS origin, mirroring the
 * backend's `{verify_base_url}/u/{code}` (see `collection_flow::flow_link`).
 * The verify SPA offers this as a copyable "resume link" — it embeds the
 * long-lived session token, so only build it from a token known to be the
 * collection bearer (the desktop entry), never a short-lived handoff token.
 */
export function sessionUrl(
  token: string,
  origin: string = typeof window !== 'undefined' ? window.location.origin : '',
): string {
  const code = encodeFlowCode(token);
  if (code) return `${stripTrailingSlash(origin)}/u/${code}`;
  // Every minted token is a uuid; anything else is only carried through as-is.
  return `${stripTrailingSlash(origin)}/verify?token=${encodeURIComponent(token)}`;
}
